import type { MarkRequired } from 'ts-essentials';

import type { BaseOptions, CompatibleStorageValue } from './types';
import { useReadFromLocalStorage } from './useReadFromLocalStorage';
import { useWriteToLocalStorage } from './useWriteToStorage';

interface SynchronizeOptions<
  T extends CompatibleStorageValue,
  PersistedParsedValue extends CompatibleStorageValue = Partial<T>,
> extends MarkRequired<
    BaseOptions<T, PersistedParsedValue>,
    'transformValueOnRead' | 'transformValueOnWrite'
  > {
  storedValue: T;
  setStoredValue: (newValue: T) => void;
  whenRef: React.MutableRefObject<boolean>;
  unversionedKey: string;
  emptyValue: T;
}

export function useSynchronizeStorage<
  T extends CompatibleStorageValue,
  PersistedParsedValue extends CompatibleStorageValue = Partial<T>,
>({
  storedValue,
  setStoredValue,
  shouldSuspendUpdateOnPageInvisibility,
  whenRef,
  readOnly,
  unversionedKey,
  version,
  duration,
  emptyValue,
  stringify,
  parse,
  transformValueOnRead,
  transformValueOnWrite,
}: SynchronizeOptions<T, PersistedParsedValue>) {
  const write = useWriteToLocalStorage(unversionedKey, storedValue, {
    whenRef,
    readOnly: readOnly || !whenRef.current,
    version,
    duration,
    stringify,
    transformValueOnWrite,
  });

  useReadFromLocalStorage(unversionedKey, emptyValue, storedValue, setStoredValue, {
    whenRef,
    duration,
    version,
    shouldSuspendUpdateOnPageInvisibility,
    parse,
    stringify,
    transformValueOnRead,
  });

  return {
    forceWrite: write,
  };
}
